import { useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { Typography, Box, Tabs, Tab } from "@mui/material";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import IncomingMailsTable from "../../components/tables/IncomingMailsTable.jsx";
import OutgoingMailsTable from "../../components/tables/OutgoingMailsTable.jsx";
import MailDetailModal from "../../components/modals/MailDetailModal.jsx";

/**
 * Mail Matching Page
 * Shows incoming mails with assignment to claims and outgoing (sent) mails
 * URL structure: /admin/emails/:mainFolder/:subFolder
 * Examples:
 *   - /admin/emails/inbox/not_assigned
 *   - /admin/emails/inbox/assigned
 *   - /admin/emails/sent/all
 *   - /admin/emails/sent/claims
 */
export default function MailMatchingPage() {
  const {
    mainFolder = "inbox",
    subFolder = "not_assigned",
    mailId,
  } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();

  const isIncoming = mainFolder === "inbox";
  const currentFolder = isIncoming ? subFolder : "not_assigned";
  const currentCategory = !isIncoming ? subFolder : "all";

  const folderDisplayNames = useMemo(
    () => ({
      not_assigned: "Nicht zugeordnet",
      assigned: "Zugeordnet",
      all: "Alle",
      claim_communication: "Schaden",
      reminder: "Erinnerungen",
      notification: "Benachrichtigungen",
      system_all: "System",
    }),
    []
  );

  const mainFolderDisplayName = isIncoming ? "Posteingang" : "Gesendet";
  const subFolderDisplayName = folderDisplayNames[subFolder] || subFolder;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "E-Mail Verwaltung", path: "/admin/emails" },
      { name: mainFolderDisplayName, path: `/admin/emails/${mainFolder}` },
      { name: subFolderDisplayName },
    ]);
  }, [
    setBreadcrumbs,
    mainFolder,
    subFolder,
    mainFolderDisplayName,
    subFolderDisplayName,
  ]);

  const navigateToFolder = useCallback(
    (newMainFolder, newSubFolder) => {
      navigate(`/admin/emails/${newMainFolder}/${newSubFolder}`, {
        replace: true,
      });
    },
    [navigate]
  );

  const handleRowClick = (mail) => {
    navigate(`/admin/emails/${mainFolder}/${subFolder}/${mail.id}`);
  };

  const handleModalClose = useCallback(() => {
    navigate(`/admin/emails/${mainFolder}/${subFolder}`, { replace: true });
  }, [navigate, mainFolder, subFolder]);

  const handleMainFolderChange = (event, newValue) => {
    const defaultSubFolder = newValue === "inbox" ? "not_assigned" : "all";
    navigateToFolder(newValue, defaultSubFolder);
  };

  const handleIncomingFolderChange = (folderKey) => {
    navigateToFolder("inbox", folderKey);
  };

  const handleOutgoingCategoryChange = (categoryKey) => {
    navigateToFolder("sent", categoryKey);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" gutterBottom>
          E-Mail Verwaltung
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Verwaltung eingehender und ausgehender E-Mails
        </Typography>
      </Box>

      {/* Main Folder Tabs (Inbox / Sent) */}
      <Box sx={{ mb: 4, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={mainFolder}
          onChange={handleMainFolderChange}
          aria-label="E-Mail Ordner"
        >
          <Tab
            label="Posteingang"
            value="inbox"
            sx={{ textTransform: "none" }}
          />
          <Tab label="Gesendet" value="sent" sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      {/* Email List */}
      {isIncoming ? (
        <IncomingMailsTable
          folder={currentFolder}
          onRowClick={handleRowClick}
          onFolderChange={handleIncomingFolderChange}
          selectedMailId={mailId}
        />
      ) : (
        <OutgoingMailsTable
          category={currentCategory}
          onRowClick={handleRowClick}
          onCategoryChange={handleOutgoingCategoryChange}
          selectedMailId={mailId}
        />
      )}

      {/* Mail Detail Modal — driven by :mailId URL param */}
      <MailDetailModal
        open={!!mailId}
        onClose={handleModalClose}
        mailId={mailId}
        isIncoming={isIncoming}
      />
    </Box>
  );
}
